import { useState } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/useAuth";
import { Modal, Tabs } from "@tw/tw-common-js";
import RolePrivilegeHistory from "../audit/RolePrivilegeHistory";

export default function RoleEdit({ roleInit, onClose, onUpdate }) {
  const { privilegeLabels } = useAuth();

  const [role, setRole] = useState(() => {
    const initial = { ...roleInit };
    if (typeof initial.privileges === "string") {
      initial.privileges = initial.privileges.split(",").map((s) => s.trim());
    }
    if (typeof initial.visibility === "string") {
      initial.visibility = initial.visibility.split(",").map((s) => s.trim());
    }
    initial.privileges = initial.privileges || [];
    initial.visibility = initial.visibility || [];
    return initial;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [selectedSection, setSelectedSection] = useState(Object.keys(privilegeLabels)[0]);

  const setValue = (key, value) => {
    setRole({ ...role, [key]: value });
  };

  const togglePrivilege = (key) => {
    const current = role.privileges || [];
    if (key === "admin") {
      if (!current.includes("admin")) {
        setValue("privileges", ["admin"]);
      } else {
        setValue("privileges", []);
      }
      return;
    } else {
      if (current.includes("admin")) {
        return;
      }
      if (current.includes(key)) {
        setValue(
          "privileges",
          current.filter((p) => p !== key)
        );
      } else {
        setValue("privileges", [...current, key]);
      }
    }
  };

  const save = async () => {
    if (!role.name) {
      setFieldErrors({ ...fieldErrors, name: "Insert a valid role name" });
      return;
    }
    setLoading(true);
    setError(null);

    // Filter privileges to only those present in privilegeLabels
    const validPrivileges = Object.values(privilegeLabels).flatMap((section) => Object.keys(section));
    const filteredPrivileges = (role.privileges || []).filter((p) => validPrivileges.includes(p));

    try {
      let response;
      // Edit
      response = await api.editRole({
        id: role.id,
        name: role.name,
        privileges: filteredPrivileges,
        visibility: role.visibility,
      });
      if (response.status === 200) {
        onUpdate(response.data);
      }
      onClose();
    } catch (err) {
      console.error("Error saving role:", err);
      setError("Failed to save role. Please try again.");
    }
    setLoading(false);
  };

  const unslug = (str) => {
    return str.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());
  };

  const getSectionCount = (section) => {
    const privKeys = Object.keys(privilegeLabels[section]);
    return role.privileges.filter((p) => privKeys.includes(p)).length;
  };

  const allSelectedInSection = () => {
    const privKeys = Object.keys(privilegeLabels[selectedSection]);
    return privKeys.every((p) => role.privileges.includes(p));
  };

  const toggleAllInSection = () => {
    const privKeys = Object.keys(privilegeLabels[selectedSection]);
    if (allSelectedInSection()) {
      setValue(
        "privileges",
        role.privileges.filter((p) => !privKeys.includes(p))
      );
    } else {
      setValue("privileges", Array.from(new Set([...role.privileges, ...privKeys])));
    }
  };

  return (
    <Modal
      close={onClose}
      className="modal-role-management"
      title={`Edit Role ${role.name}`}
      footer={
        <>
          <div className="btn-bar-left"></div>
          <div className="btn-bar-right">
            <button className="link" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button className="blue" onClick={save} disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </button>
          </div>
        </>
      }
    >
      {error && <p className="error">{error}</p>}

      <Tabs
        tabs={[
          {
            id: "privileges",
            name: "Privileges",
            component: (
              <div className="privileges-grid">
                {/* Left: Sections */}
                <div className="privileges-sections">
                  <div className="privileges-list-header">Sections</div>
                  <div className="privileges-sections-list">
                    {Object.keys(privilegeLabels).map((section) => (
                      <div
                        key={section}
                        className={`privileges-section-item${selectedSection === section ? " selected" : ""}`}
                        onClick={() => setSelectedSection(section)}
                      >
                        {unslug(section)} <span className="privileges-section-count">({getSectionCount(section)})</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Center: Privileges of selected section */}
                <div className="privileges-list">
                  <div className="privileges-list-header">Select privileges</div>
                  <div className="privileges-section-items">
                    {Object.keys(privilegeLabels[selectedSection]).map((key) => (
                      <div className="input-group" key={key}>
                        <input type="checkbox" checked={role.privileges.includes(key)} onChange={() => togglePrivilege(key)} />
                        <div className="privilege-label-desc">
                          <span className="privilege-label">{privilegeLabels[selectedSection][key].label}</span>
                          <div className="privilege-description">{privilegeLabels[selectedSection][key].description}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Right: Select all */}
                <div className="privileges-select-all">
                  <label>
                    <input type="checkbox" checked={allSelectedInSection()} onChange={toggleAllInSection} />
                    Select all
                  </label>
                </div>
              </div>
            ),
          },
          {
            id: "history",
            name: "Change History",
            component: <RolePrivilegeHistory roleId={role.id} />,
          },
        ]}
      />
    </Modal>
  );
}
